import { useCallback, useEffect, useState } from 'react';

export function useFullscreenController(videoContainerRef: React.RefObject<HTMLElement | null>) {
    const [fullscreenSupported, setFullscreenSupported] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [isFauxFullscreen, setFauxFullScreen] = useState<boolean>(false);

    // Check if the Fullscreen API is supported
    useEffect(() => {
        setFullscreenSupported(typeof document !== 'undefined' && document.fullscreenEnabled === true);
    }, []);

    // Sync React state with actual fullscreen state
    useEffect(() => {
        if (!fullscreenSupported) return;

        const onFullscreenChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [fullscreenSupported]);

    // Toggle fullscreen (native or faux)
    const toggleFullscreen = useCallback(async () => {
        if (!fullscreenSupported || !videoContainerRef.current) {
            // iOS or unsupported browser fallback
            setFauxFullScreen((prev) => !prev);
            return;
        }

        try {
            if (!document.fullscreenElement) {
                await videoContainerRef.current.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch (err) {
            console.error('Fullscreen toggle failed:', err);
        }
    }, [fullscreenSupported, videoContainerRef]);

    // Handle Esc to exit faux fullscreen (normal fullscreen exit by Esc is handled by the browser)
    useEffect(() => {
        if (!isFauxFullscreen) return;

        const handler = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') {
                setFauxFullScreen(false);
            }
        };

        globalThis.window.addEventListener('keydown', handler);
        return () => globalThis.window.removeEventListener('keydown', handler);
    }, [isFauxFullscreen]);

    return {
        fullscreenSupported,
        isFullscreen,
        isFauxFullscreen,
        toggleFullscreen
    };
}
